import React, { useMemo } from "react";

import { exists } from "@examspark/common";
import { Callout, InfoBanner, SelectInput } from "@examspark/common-ui";
import { Course, CourseCalendar } from "@examspark/types";

import { useQueryGetAllCalendarsForCourse } from "../../api/operations/calendar";
import { useQueryListContentSharedToWorkspace } from "../../api/operations/sharing";
import { useAppContext } from "../../providers/AppContext";

const CourseCalendarSelect = (props: {
  course: Course | undefined;
  value?: { id: string; name?: string | null };
  onChange?: (value: CourseCalendar | undefined) => void;
  disabled?: boolean;
  includeArchivedCalendars?: boolean;
}) => {
  const { data: calendars, isFetched } = useQueryGetAllCalendarsForCourse(
    props.course,
    {
      includeArchivedCalendars: props.includeArchivedCalendars,
    },
  );

  const foundActiveValue = calendars?.find((cal) => cal.id === props.value?.id);

  return (
    <>
      <CourseCalendarSelectInput
        disabled={props.disabled}
        calendars={calendars ?? []}
        activeCalendar={foundActiveValue}
        setActiveCalendar={(calendar) => {
          props.onChange?.(calendar);
        }}
      />
      {!foundActiveValue && props.value?.name && isFetched ? (
        <Callout>
          <InfoBanner>
            Your classroom calendar, {props.value?.name}, is no longer available
            and cannot be re-selected if changed.
          </InfoBanner>
        </Callout>
      ) : null}
    </>
  );
};

const CourseCalendarSelectInput = ({
  calendars,
  activeCalendar,
  setActiveCalendar,
  disabled,
}: {
  calendars: CourseCalendar[];
  activeCalendar: CourseCalendar | undefined;
  setActiveCalendar: (cal: CourseCalendar | undefined) => void;
  disabled?: boolean;
}) => {
  const calendarsByWorkspace = useMemo(() => {
    const _calendarsByWorkspace: Record<string, CourseCalendar[]> = {};
    calendars.forEach((cal) => {
      if (!_calendarsByWorkspace[cal.workspaceId]) {
        _calendarsByWorkspace[cal.workspaceId] = [];
      }
      _calendarsByWorkspace[cal.workspaceId].push(cal);
    });
    return _calendarsByWorkspace;
  }, [calendars]);

  const [context] = useAppContext();

  const workspaceShares = useQueryListContentSharedToWorkspace();

  const workspaceNameMap =
    workspaceShares.data
      ?.map((workspaceShare) => workspaceShare.workspace)
      .filter(exists)
      .reduce(
        (acc, workspace) => {
          acc[workspace.id] = workspace.name ?? workspace.id;
          return acc;
        },
        {} as Record<string, string>,
      ) ?? {};

  // add the current workspace as well
  workspaceNameMap[context.workspaceUser?.workspace?.id ?? ""] = "My Workspace";

  return (
    <SelectInput
      isClearable
      disabled={disabled}
      options={Object.entries(calendarsByWorkspace).map(
        ([workspaceId, calendars]) => ({
          label: workspaceNameMap[workspaceId] ?? workspaceId,
          options: calendars.map((cal) => {
            const periodLabel =
              cal.calendar?.gradingPeriodLength === "6_WEEKS"
                ? "6 Weeks"
                : "9 Weeks";
            const archivedLabel =
              cal.calendar?.isArchived || cal.isArchived ? "(Archived)" : "";
            const label = [
              cal.name ?? "Untitled Course Calendar",
              `(${periodLabel})`,
              archivedLabel,
            ].join(" ");

            return {
              value: cal.id,
              label,
            };
          }),
        }),
      )}
      value={activeCalendar?.id}
      onChange={(e) =>
        setActiveCalendar(
          calendars.find((cal) => cal.id === e?.value) ?? undefined,
        )
      }
    />
  );
};

export default CourseCalendarSelect;
